import React, { useContext, useEffect, useState } from 'react'
import { AuthenticationContext } from 'app/Contexts/AuthenticationProvider'
import { FormProfile } from './FormProfile'
import {
  restoreDraftFromLocalStorage,
  deleteDraftFromLocalStorage,
  saveDraftToLocalStorage,
} from './draft'
import { formEmptyValues } from './utils'
import { joinApiDataToLocalValues, transformFormValueInApiInput } from './api'
import { endpoints } from 'app/api/endpoints'
import { useGet, useMutate } from 'restful-react'
import {
  ProfileFormSteps,
  Supplier,
  SupplierInput,
  SupplierUpdateApiError,
  SupplierUserDataApiError,
} from 'EstraSuppliers'
import { useHistory } from 'react-router-dom'
import { routes } from 'app/routes'
import { HeaderContext } from 'app/Contexts/HeaderProvider'
import styled from 'styled-components'
import { Container } from 'app/components/ui/Container'
import { Text } from 'app/components/ui/Text'
import { Button } from 'app/components/ui/Button'
import { ApiErrorMessage } from 'app/components/ui/form/ApiErrorMessage'
import { ProfileStepsNav } from 'app/components/shared/ProfileStepsNav'

export const ProfileEdit: React.FC = () => {
  const authCtx = useContext(AuthenticationContext)
  const headerCtx = useContext(HeaderContext)

  const { push } = useHistory()

  const userId = authCtx?.actor?.id

  if (!userId) return null

  const supplierApiData = authCtx?.actor?.supplier

  if (!supplierApiData) return null

  const isProfileDataCompleted = supplierApiData.status !== 'invited'

  // getting data saved from local storage merged with empty defaults
  const storedDraft = userId
    ? restoreDraftFromLocalStorage(userId, formEmptyValues)
    : formEmptyValues

  // merging api data with locally saved draft
  const mergedDraftWithApiData = supplierApiData
    ? joinApiDataToLocalValues(supplierApiData, storedDraft)
    : storedDraft

  // ACCEPT INVITE MUTATION
  const {
    mutate: submitFormToApi,
    loading: isSendingFormToApi,
    error,
  } = useMutate<Supplier, {}, {}, SupplierInput>({
    verb: 'PUT',
    path: endpoints.editProfile,
  })

  // SEND REMINDER EMAIL
  const {
    loading: isSendingReminderEmail,
    refetch: sendReminderEmail,
    error: sendReminderEmailError,
  } = useGet({
    path: endpoints.sendReminderEmail,
    lazy: true, // Execute call only on refetch
  })

  // Check if the error returned from the Api is an expected error with a validation inside
  // or another Api Error
  // TS TypeGuards
  function isSupplierUpdateApiError(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error: any
  ): error is SupplierUpdateApiError {
    const supplierUpdateError = error as SupplierUpdateApiError
    return (
      supplierUpdateError.data?.supplier !== undefined ||
      supplierUpdateError.data?.holder_address !== undefined ||
      supplierUpdateError.data?.technical_director_address !== undefined ||
      supplierUpdateError.data?.bank_account_1 !== undefined ||
      supplierUpdateError.data?.bank_account_2 !== undefined
    )
  }

  // Check if there is at least one error in the first step
  function isSupplierUserDataApiError(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error: any
  ): error is SupplierUserDataApiError {
    const supplierUpdateError = error as SupplierUserDataApiError

    // Check if the supplier object is present, and if at least one field error of the first step is present,
    // excluding the general fields belonging to the second step
    return (
      supplierUpdateError.data?.supplier !== undefined &&
      Object.keys(supplierUpdateError.data.supplier).filter(
        k =>
          k !== 'company_dm_letters' &&
          k !== 'standard_iso_9001' &&
          k !== 'standard_iso_14001' &&
          k !== 'standard_ohsas18001' &&
          k !== 'standard_iso_45001' &&
          k !== 'standard_iso_iec_27001' &&
          k !== 'standard_sa8000' &&
          k !== 'standard_other' &&
          k !== 'privacy_required' &&
          k !== 'privacy_optional_internal_marketing' &&
          k !== 'privacy_optional_external_marketing'
      ).length !== 0
    )
  }

  // Steps

  const [activeStep, setActiveStep] = useState<ProfileFormSteps>('user_data')

  const isUserDataStep = activeStep === 'user_data'

  const headerTitle = 'Area fornitori Marketing'
  const headerUserDataSubTitle =
    'Registra i dati anagrafici della tua attività e procedi con l’attivazione'
  const headerAccountingDataSubTitle =
    'Manca poco, devi solo indicare i tuoi dati contabili'
  const userDataText =
    '... è arrivato il momento di compilare l’anagrafica della tua attività.\n' +
    'Lo sappiamo, il modulo è molto lungo e articolato e potrebbe richiedere dati che non hai immediatamente disponibili.' +
    'Tuttavia, è necessario compilarlo tutto, per cui abbiamo attivato una funzione di “bozza” che, utilizzando il pulsante “SALVA I DATI” in fondo alla pagina,  ti consentirà di riprendere la compilazione in più momenti, a tuo comodo.\n' +
    '<b>Devi completare tutti gli step per  ricevere richieste di preventivo.</b>\n\n' +
    'Se non vuoi procedere adesso alla compilazione, puoi chiudere questa pagina usando il pulsante qui sotto e ritornare più tardi, accedendo dal sito www.estra.it/appalti'
  const accountingDataText =
    'Se non vuoi procedere adesso alla compilazione, puoi chiudere questa pagina usando il pulsante qui sotto e ritornare più tardi, accedendo dal sito www.estra.it/appalti'

  useEffect(() => {
    if (headerCtx) {
      headerCtx.updateTitles(headerTitle)
      headerCtx.updateSubtitleVariant('login_signup')
    }
  }, [])

  useEffect(() => {
    if (headerCtx) {
      const headerSubtitle = isUserDataStep
        ? headerUserDataSubTitle
        : headerAccountingDataSubTitle
      headerCtx.updateTitles(headerTitle, headerSubtitle)
    }
  }, [activeStep])

  return (
    <ProfileWrapper>
      <Container>
        <ProfileStepsNav currentStep={activeStep}></ProfileStepsNav>
        <HeaderWrapper>
          <Text
            variant='s24'
            mb={2}
            dangerouslySetInnerHTML={{
              __html: isUserDataStep ? userDataText : accountingDataText,
            }}
          ></Text>
          <Button
            onClick={() =>
              sendReminderEmail().then(() => {
                push(routes.profile.makeUrl())
              })
            }
          >
            {!isSendingReminderEmail
              ? 'CHIUDI LA PAGINA E MANDA UN PROMEMORIA'
              : 'Caricamento...'}
          </Button>
          {sendReminderEmailError ? (
            <ApiErrorMessage>{sendReminderEmailError}</ApiErrorMessage>
          ) : null}
        </HeaderWrapper>
      </Container>

      <FormProfile
        onSaveDraft={(values, actions) => {
          // Save data to local storage
          if (!isProfileDataCompleted) {
            saveDraftToLocalStorage(values, userId)
          }

          // Api call to submit form, it's called at the end of every step
          // On the first step is used only to get validation errors
          submitFormToApi(transformFormValueInApiInput(values))
            .then(() => {
              // Go to second step
              if (isUserDataStep) {
                setActiveStep('accounting_data')
                window.scrollTo(0, 0)
              } else {
                deleteDraftFromLocalStorage(userId || 0)
                if (authCtx && authCtx.refetchActor) {
                  authCtx.refetchActor()
                }
                push(routes.profile.makeUrl())
              }
            })
            .catch((error: SupplierUpdateApiError) => {
              // Go to second step only if there are no errors on first step
              // and if the Api error is of validation and not a generic one
              if (
                isUserDataStep &&
                isSupplierUpdateApiError(error) &&
                !isSupplierUserDataApiError(error)
              ) {
                setActiveStep('accounting_data')
                window.scrollTo(0, 0)
                actions.setErrors({})
              }
            })
        }}
        userEmail={authCtx?.actor?.email}
        initialFormData={mergedDraftWithApiData}
        isSavingForm={isSendingFormToApi}
        apiError={error as SupplierUpdateApiError} // Will be null if not a validation error
        activeStep={activeStep}
        setActiveStep={activeStep => {
          setActiveStep(activeStep)
        }}
      />
      {error && !isSupplierUpdateApiError(error) ? (
        <ErrorWrapper>
          <ApiErrorMessage></ApiErrorMessage>
        </ErrorWrapper>
      ) : null}
    </ProfileWrapper>
  )
}

const ProfileWrapper = styled.div``

const HeaderWrapper = styled.div`
  text-align: center;

  display: flex;
  flex-direction: column;
  align-items: center;

  margin-bottom: 6rem;
`

const ErrorWrapper = styled.div`
  display: flex;
  justify-content: center;
`
